// pages/mobile/InboxPage.js
// Messages inbox (mockup 3a).
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { LoadState, StatusPill, TabBar } from './MobileChrome';
import { BellIcon, PinIcon, StandDownIcon, TruckIcon, WarningIcon } from './icons';
import { DISPATCH_TYPE_LABELS, formatDay, formatSent, formatTime } from './format';
import { fetchMessages, useMobileData } from './mobileApi';
import { isInstalled, notificationPermission } from './installState';
import { showOutstandingCount } from './appBadge';
import { usePushConnection } from './pushSubscription';
import styles from './mobile.module.css';

const KINDS = {
  dispatch: { label: 'Dispatch', icon: <TruckIcon />, iconClass: styles.iconDispatch, kindClass: styles.kindDispatch, path: 'dispatches' },
  warning: { label: 'Message', icon: <WarningIcon />, iconClass: styles.iconWarning, kindClass: styles.kindWarning, path: 'warnings' },
  stand_down: { label: 'Stand down', icon: <StandDownIcon />, iconClass: styles.iconStandDown, kindClass: styles.kindStandDown, path: 'warnings' },
  request: { label: 'Service request', icon: <PinIcon />, iconClass: styles.iconRequest, kindClass: styles.kindRequest, path: 'requests' },
};

const describe = (message) => {
  switch (message.kind) {
    case 'dispatch':
      return {
        title: `Report by ${formatTime(message.dispatched_for)} ${formatDay(message.dispatched_for).split(',')[0]}`,
        subtitle: [
          message.vehicle,
          `${message.site_count} site${message.site_count === 1 ? '' : 's'}`,
          message.storm && [message.storm.type, message.storm.severity].filter(Boolean).join(', '),
        ].filter(Boolean).join(' · '),
        label: DISPATCH_TYPE_LABELS[message.dispatch_type] || 'Dispatch',
      };
    case 'request':
      return { title: [message.request_type, message.site_name].filter(Boolean).join(' · '), subtitle: '' };
    default:
      return { title: message.preview || KINDS[message.kind].label, subtitle: '' };
  }
};

const MessageCard = ({ message }) => {
  const kind = KINDS[message.kind];
  const { title, subtitle, label } = describe(message);
  return (
    <Link to={`/m/${kind.path}/${message.id}`} className={styles.messageCard}>
      <span className={`${styles.messageIcon} ${kind.iconClass}`}>{kind.icon}</span>
      <span className={styles.messageBody}>
        <span className={styles.messageMeta}>
          <span className={`${styles.messageKind} ${kind.kindClass}`}>{label || kind.label}</span>
          <span className={styles.messageTime}>{formatSent(message.sent_at)}</span>
        </span>
        <span className={styles.messageTitle}>{title}</span>
        {subtitle && <span className={styles.messageSubtitle}>{subtitle}</span>}
        <span className={styles.messageStatus}>
          <StatusPill status={message.status} needsAnswer={message.needs_answer} neutralLabel="No answer needed" />
        </span>
      </span>
    </Link>
  );
};

const InboxPage = () => {
  const { data, error, loading, reload } = useMobileData(fetchMessages);
  const [tab, setTab] = useState('needs');
  const pushConnection = usePushConnection();
  const messages = data?.messages || [];
  const needingAnswer = messages.filter((message) => message.needs_answer);

  // Keep the Home Screen icon's number in step with the inbox (it refreshes itself).
  useEffect(() => {
    if (data) showOutstandingCount(needingAnswer.length);
  }, [data, needingAnswer.length]);

  const shown = tab === 'needs' ? needingAnswer : messages;
  const setupDone = isInstalled() && notificationPermission() === 'granted' && pushConnection === 'subscribed';

  return (
    <main className={`${styles.page} ${styles.pageWithTabs}`}>
      <header className={styles.pageHeader}>
        <div className={styles.pageHeaderRow}>
          <div>
            {data?.staff_name && <p className={styles.greeting}>Hi, {data.staff_name}</p>}
            <h1 className={styles.pageTitle}>Messages</h1>
          </div>
        </div>
        <div role="tablist" aria-label="Messages" className={styles.segmented}>
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'needs'}
            className={tab === 'needs' ? `${styles.segment} ${styles.segmentActive}` : styles.segment}
            onClick={() => setTab('needs')}
          >
            Needs answer
            {needingAnswer.length > 0 && <span className={styles.countBadge}>{needingAnswer.length}</span>}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'all'}
            className={tab === 'all' ? `${styles.segment} ${styles.segmentActive}` : styles.segment}
            onClick={() => setTab('all')}
          >
            All messages
          </button>
        </div>
      </header>

      <div className={styles.content}>
        {!setupDone && (
          <Link to="/m/setup" className={styles.banner}>
            <BellIcon />
            <span>Finish setting up this phone so dispatches reach you.</span>
            <span className={styles.bannerAction}>Set up</span>
          </Link>
        )}
        {(loading && !data) || error ? (
          <LoadState loading={loading && !data} error={error} onRetry={reload} />
        ) : shown.length === 0 ? (
          <p className={styles.emptyState}>
            {tab === 'needs' ? "You're all caught up. Nothing needs an answer." : 'No messages yet.'}
          </p>
        ) : (
          shown.map((message) => <MessageCard key={`${message.kind}-${message.id}`} message={message} />)
        )}
      </div>
      <TabBar />
    </main>
  );
};

export default InboxPage;
